import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import { useFilterStore } from '@/stores/filterStore';
import { salesApi } from '@/services/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { DateRangeSelector } from '@/components/dashboard/DateRangeSelector';
import type { Transaction } from '@/types';

export function RawData() {
  const { t, i18n } = useTranslation();
  const { preset, startDate, endDate } = useDateRangeStore();
  const { searchText, setSearchText } = useFilterStore();
  const [localSearch, setLocalSearch] = useState(searchText);

  const { data, isLoading } = useQuery<{ transactions: Transaction[]; total: number }>({
    queryKey: ['transactions', preset, startDate.toISOString(), endDate.toISOString()],
    queryFn: () =>
      salesApi.getTransactions({
        preset,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 1000,
      }),
    refetchInterval: 60000,
  });

  // Filter transactions based on search text
  const filteredTransactions = useMemo(() => {
    if (!data?.transactions) return [];
    if (!searchText.trim()) return data.transactions;

    const searchLower = searchText.toLowerCase();
    return data.transactions.filter((tx) => {
      return (
        tx.companyName?.toLowerCase().includes(searchLower) ||
        tx.salesAgentName?.toLowerCase().includes(searchLower) ||
        tx.invoiceNumber?.toLowerCase().includes(searchLower) ||
        tx.email?.toLowerCase().includes(searchLower) ||
        tx.contactName?.toLowerCase().includes(searchLower) ||
        tx.phone?.includes(searchLower) ||
        tx.paymentMethod?.toLowerCase().includes(searchLower) ||
        tx.businessDescription?.toLowerCase().includes(searchLower) ||
        tx.amount?.toString().includes(searchLower)
      );
    });
  }, [data?.transactions, searchText]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchText(localSearch);
  };

  const handleClearSearch = () => {
    setLocalSearch('');
    setSearchText('');
  };

  // Update local search when store changes
  useMemo(() => {
    setLocalSearch(searchText);
  }, [searchText]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('rawData.title')}</h1>
        <DateRangeSelector />
      </div>

      {/* Search Filter */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={localSearch}
              onChange={handleSearchChange}
              placeholder={t('rawData.searchPlaceholder')}
              className="w-full ps-10 pe-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {localSearch && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('common.search')}
          </button>
        </form>
        {searchText && (
          <div className="mt-2 text-sm text-gray-500">
            {t('rawData.showing')} {filteredTransactions.length} {t('rawData.of')} {data?.transactions?.length || 0} {t('rawData.results')}
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.company')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.agent')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.amount')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.paymentMethod')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.paymentDate')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.status')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.contact')}
                </th>
                <th className="px-4 py-3 text-start text-sm font-semibold text-gray-600">
                  {t('rawData.columns.email')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                [...Array(10)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    {t('rawData.noData')}
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((tx) => (
                  <tr key={tx._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {tx.companyName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.salesAgentName}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {formatCurrency(tx.amount, i18n.language)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.paymentMethod || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.paymentDate ? formatDate(tx.paymentDate, 'dd/MM/yyyy', i18n.language) : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          tx.isPending
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {tx.isPending ? t('rawData.pending') : t('rawData.completed')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.contactName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {tx.email || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
